import { useEffect, useMemo } from 'react';

import { Grid } from '@mui/material';

import { CuredApiPaths } from 'models/enums';
import Card from 'pages/InsightsPage/dashboards/Card';
import { FiltersForApiState } from 'pages/InsightsPage/dashboards/filtering/types';
import useFilteredData from 'pages/InsightsPage/dashboards/filtering/useFilteredData';
import DataGrid from 'pages/InsightsPage/visualizations/DataGrid';
import {
    getHeaderName,
    getValueFormatter,
    useGetRows,
    ValueFormatTypes,
} from 'pages/InsightsPage/visualizations/DataGrid/utils';

import AutoResponseCards from './AutoResponseCards';

type Props = {
    filtersForApi: FiltersForApiState;
};

const SMSMetricsBySendCard = ({ filtersForApi }: Props) => {
    const { getData, isError, isLoading, data } = useFilteredData({
        key: 'sms-dashboard/metrics-by-send',
        path: CuredApiPaths.REPORT_SMS_METRICS,
    });

    useEffect(() => {
        getData(filtersForApi);
    }, [filtersForApi, getData]);

    const tableData = data as {}[];

    const columns: any = useMemo(() => {
        if (!tableData?.length) return;

        const columnFields = Object.keys(tableData?.[0]);
        const columns = columnFields?.map((field) => ({
            field,
            flex: 1,
            headerName: getHeaderName(field),
            valueFormatter: ({ value }: { value?: string }) => {
                const getType = () => {
                    if (field.includes('rate')) return ValueFormatTypes.PERCENTAGE;
                    return null;
                };
                return getValueFormatter({ type: getType(), value });
            },
        }));

        return columns;
    }, [tableData]);

    const rows = useGetRows(tableData);

    // We only want to show autoresponse cards if the dashboard is filtered
    // on one campaign that is interactive (has autoresponses)
    const showAutoResponseCards = data?.length === 1 && data[0].interactive === 'Yes';

    return (
        <Grid container item spacing={2} xs={12}>
            <Grid item xs={12}>
                <Card isError={isError} isLoading={isLoading} label="Text Message Metrics by Send">
                    Track metrics by send and follow up with your audience
                    <DataGrid columns={columns} rows={rows} />
                </Card>
            </Grid>
            {showAutoResponseCards && <AutoResponseCards statsData={data[0]} filtersForApi={filtersForApi} />}
        </Grid>
    );
};

export default SMSMetricsBySendCard;
